import React, { useState, useEffect, useMemo, useCallback, useContext, lazy, Suspense } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Chip,
  Collapse,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import TaskIcon from '@mui/icons-material/Task';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DirectionsIcon from '@mui/icons-material/Directions';
import { dashboardApi, activitiesApi, projectsApi, purchaseOrdersApi } from '../services/api';
import PersonalTodoList from '../components/PersonalTodoList';
import DashboardTimeline from '../components/DashboardTimeline';
import { calculateEndDate } from '../utils/dateUtils';
import { AppContext } from '../App';

// Lazy load the chart component to reduce initial bundle size
const PerformanceChart = lazy(() => import('../components/PerformanceChart'));

const ListSkeleton = () => (
  <List>
    {[...Array(3)].map((_, index) => (
      <React.Fragment key={index}>
        <ListItem>
          <ListItemText
            primary={<Skeleton width="60%" />}
            secondary={
              <Box>
                <Skeleton width="80%" />
                <Skeleton width="40%" />
              </Box>
            }
          />
        </ListItem>
        <Divider />
      </React.Fragment>
    ))}
  </List>
);

const ActivityListItem = React.memo(({ activity, formatDate, getStatusColor, onComplete, loadingComplete }) => (
  <React.Fragment>
    <ListItem
      sx={{ 
        display: 'flex',
        alignItems: 'flex-start',
        pr: 1
      }}
    >
      <Box 
        component={RouterLink}
        to={`/projects/${activity.project_id}?tab=activities&activityId=${activity.activity_id}`}
        sx={{ 
          flex: 1, 
          textDecoration: 'none', 
          color: 'inherit',
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          p: 1,
          ml: -1,
          cursor: 'pointer'
        }}
      >
        <ListItemText
          primary={activity.title}
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary">
                Project: {activity.project_organisation}
              </Typography>
              {activity.quote_reference && (
                <Typography variant="body2" color="text.secondary">
                  Quote Ref: {activity.quote_reference}
                </Typography>
              )}
              <Chip 
                label={formatDate(activity.due_date)}
                color={getStatusColor(activity.due_date)}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          }
        />
      </Box>
      <Tooltip title="Mark as Complete">
        <IconButton
          size="small"
          color="success"
          onClick={(e) => {
            e.stopPropagation();
            onComplete(activity.activity_id);
          }}
          disabled={loadingComplete === activity.activity_id}
          sx={{ mt: 1 }}
        >
          {loadingComplete === activity.activity_id ? (
            <CircularProgress size={20} />
          ) : (
            <CheckCircleOutlineIcon />
          )}
        </IconButton>
      </Tooltip>
    </ListItem>
    <Divider />
  </React.Fragment>
));

const SalesOrderListItem = React.memo(({ project, formatDate, getStatusColor, onComplete, loadingComplete, onDirections }) => {
  const effectiveDueDate = useMemo(() => {
    if (project.start_date && project.duration_in_days) {
      const endDate = calculateEndDate(
        project.start_date, 
        project.duration_in_days, 
        project.working_sat, 
        project.working_sun
      );
      return endDate ? endDate.toISOString() : project.due_date;
    }
    return project.due_date;
  }, [project]);

  return (
  <React.Fragment>
    <ListItem
      sx={{ 
        display: 'flex',
        alignItems: 'flex-start',
        pr: 1
      }}
    >
      <Box 
        component={RouterLink}
        to={`/projects/${project.project_id}`}
        sx={{ 
          flex: 1, 
          textDecoration: 'none', 
          color: 'inherit',
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          p: 1,
          ml: -1,
          cursor: 'pointer'
        }}
      >
        <ListItemText
          primary={project.organisation_name}
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary">
                Quote Ref: {project.quote_reference || 'N/A'} | ID: {project.project_id}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {project.address_line1}, {project.city}
                </Typography>
                <Tooltip title="Get Directions">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDirections(project);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <DirectionsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {project.start_date && (
                  <Chip 
                    label={`Start: ${new Date(project.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
                <Chip 
                  label={formatDate(effectiveDueDate)}
                  color={getStatusColor(effectiveDueDate)}
                  size="small"
                />
                {!project.has_rams && (
                  <Chip 
                    label="No RAMS"
                    size="small"
                    sx={{ 
                      bgcolor: 'grey.400',
                      color: 'text.primary',
                      fontWeight: 500
                    }}
                  />
                )}
              </Box>
            </Box>
          }
        />
      </Box>
      <Tooltip title="Mark as Complete">
        <IconButton
          size="small"
          color="success"
          onClick={(e) => {
            e.stopPropagation();
            onComplete(project.project_id);
          }}
          disabled={loadingComplete === project.project_id}
          sx={{ mt: 1 }}
        >
          {loadingComplete === project.project_id ? (
            <CircularProgress size={20} />
          ) : (
            <CheckCircleOutlineIcon />
          )}
        </IconButton>
      </Tooltip>
    </ListItem>
    <Divider />
  </React.Fragment>
  );
});

const PurchaseOrderListItem = React.memo(({ po, formatDate, getStatusColor, onComplete, loadingComplete }) => (
  <React.Fragment>
    <ListItem
      sx={{ 
        display: 'flex',
        alignItems: 'flex-start',
        pr: 1
      }}
    >
      <Box 
        component={RouterLink}
        to={`/purchase-orders/${po.purchase_order_id}`}
        sx={{ 
          flex: 1, 
          textDecoration: 'none', 
          color: 'inherit',
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          p: 1,
          ml: -1,
          cursor: 'pointer'
        }}
      >
        <ListItemText
          primary={`PO: ${po.reference || `#${po.purchase_order_id}`}`}
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary">
                Vendor: {po.vendor_name || 'Unknown'}
              </Typography>
              {po.project_id && (
                <Typography variant="body2" color="text.secondary">
                  Project ID: {po.project_id}
                </Typography>
              )}
              <Chip 
                label={formatDate(po.due_date)}
                color={getStatusColor(po.due_date)}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          }
        />
      </Box>
      <Tooltip title="Mark as Complete">
        <IconButton
          size="small"
          color="success"
          onClick={(e) => {
            e.stopPropagation();
            onComplete(po.purchase_order_id);
          }}
          disabled={loadingComplete === po.purchase_order_id}
          sx={{ mt: 1 }}
        >
          {loadingComplete === po.purchase_order_id ? (
            <CircularProgress size={20} />
          ) : (
            <CheckCircleOutlineIcon />
          )}
        </IconButton>
      </Tooltip>
    </ListItem>
    <Divider />
  </React.Fragment>
));

function Dashboard() {
  const { currentUser } = useContext(AppContext);
  const [dashboardData, setDashboardData] = useState({
    upcoming_activities: [],
    upcoming_sales_orders: [],
    upcoming_purchase_orders: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(null); // Track which item is being marked as complete

  // Helper function to get address string for directions
  const getAddressString = (project) => {
    const parts = [
      project.address_line1,
      project.address_line2,
      project.city,
      project.postcode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Function to open directions in maps
  const openDirections = (project) => {
    const address = getAddressString(project);
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://maps.google.com/maps?q=${encodedAddress}`, '_blank');
    }
  };

  // Memoized utility functions for better performance
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'No date set';
    const date = new Date(dateString);
    
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    return `Due: ${formattedDate}`;
  }, []);

  const getStatusColor = useCallback((dateString) => {
    if (!dateString) return 'default';
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'error';
    if (diffDays <= 1) return 'warning';
    if (diffDays <= 7) return 'info';
    return 'success';
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await dashboardApi.getDashboardData();
      setDashboardData({
        upcoming_activities: response.data.upcoming_activities || [],
        upcoming_sales_orders: response.data.upcoming_sales_orders || [],
        upcoming_purchase_orders: response.data.upcoming_purchase_orders || []
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggleAnalytics = useCallback(async () => {
    if (!showAnalytics && !analyticsData) {
      // Load analytics data for the first time - filter by user's current department
      try {
        setLoadingAnalytics(true);
        const department = currentUser?.department || null;
        const response = await dashboardApi.getAnalytics(department);
        setAnalyticsData(response.data.monthly_stats);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        // Don't prevent showing the section, just show no data
      } finally {
        setLoadingAnalytics(false);
      }
    }
    setShowAnalytics(!showAnalytics);
  }, [showAnalytics, analyticsData, currentUser?.department]);

  const handleCompleteActivity = useCallback(async (activityId) => {
    try {
      setLoadingComplete(activityId);
      await activitiesApi.markAsComplete(activityId);
      // Refresh dashboard data
      await fetchDashboardData();
    } catch (err) {
      console.error('Error marking activity as complete:', err);
      setError('Failed to mark activity as complete');
    } finally {
      setLoadingComplete(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompleteSalesOrder = useCallback(async (projectId) => {
    try {
      setLoadingComplete(projectId);
      await projectsApi.markCompleted(projectId);
      // Refresh dashboard data
      await fetchDashboardData();
    } catch (err) {
      console.error('Error marking sales order as complete:', err);
      setError('Failed to mark sales order as complete');
    } finally {
      setLoadingComplete(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompletePurchaseOrder = useCallback(async (purchaseOrderId) => {
    try {
      setLoadingComplete(purchaseOrderId);
      await purchaseOrdersApi.markAsComplete(purchaseOrderId);
      // Refresh dashboard data
      await fetchDashboardData();
    } catch (err) {
      console.error('Error marking purchase order as complete:', err);
      setError('Failed to mark purchase order as complete');
    } finally {
      setLoadingComplete(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Memoized computed values
  const incompleteActivities = useMemo(() => 
    dashboardData.upcoming_activities.filter(a => !a.is_completed), 
    [dashboardData.upcoming_activities]
  );
  
  const incompleteSalesOrders = useMemo(() => 
    dashboardData.upcoming_sales_orders.filter(p => p.status !== 'Completed'), 
    [dashboardData.upcoming_sales_orders]
  );
  
  const incompletePurchaseOrders = useMemo(() => 
    dashboardData.upcoming_purchase_orders.filter(po => po.status !== 'Completed'), 
    [dashboardData.upcoming_purchase_orders]
  );

  const timelineProjects = useMemo(() => dashboardData.upcoming_sales_orders, [dashboardData.upcoming_sales_orders]);

  const activityCount = incompleteActivities.length;
  const salesOrderCount = incompleteSalesOrders.length;
  const purchaseOrderCount = incompletePurchaseOrders.length;

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="60%" />
        </Box>
        
        <Paper sx={{ p: 3, mb: 4 }}>
          <Skeleton variant="text" width="200px" sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Skeleton variant="rectangular" width={200} height={40} />
            <Skeleton variant="rectangular" width={200} height={40} />
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {[...Array(3)].map((_, index) => (
            <Grid item xs={12} lg={6} key={index}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Skeleton variant="text" width="300px" sx={{ mb: 2 }} />
                <ListSkeleton />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

      if (error) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 2 }}>
          <Typography color="error">{error}</Typography>
          <Button variant="contained" onClick={fetchDashboardData} sx={{ mt: 2 }}>
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Header Section */}
      <Paper 
        elevation={2}
        sx={{ 
          p: 3, 
          mb: 4,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 0.5 }}>
              Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your construction projects overview
            </Typography>
          </Box>
          
          {/* Quick Actions */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/projects?status=Quote"
              startIcon={<AssignmentIcon />}
              sx={{ 
                fontWeight: 500,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              View Quotes
            </Button>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/projects?status=Sales%20Order"
              startIcon={<BuildIcon />}
              sx={{ 
                fontWeight: 500,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              View Sales Orders
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Timeline View */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <DashboardTimeline 
          activities={dashboardData.upcoming_activities}
          projects={timelineProjects}
          purchaseOrders={dashboardData.upcoming_purchase_orders}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Upcoming Activities */}
        <Grid item xs={12} lg={6}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TaskIcon sx={{ mr: 1 }} />
              Upcoming Activities ({activityCount})
            </Typography>
            {activityCount > 0 ? (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {incompleteActivities.map((activity) => (
                  <ActivityListItem
                    key={activity.activity_id}
                    activity={activity}
                    formatDate={formatDate}
                    getStatusColor={getStatusColor}
                    onComplete={handleCompleteActivity}
                    loadingComplete={loadingComplete}
                  />
                ))}
              </List>
            ) : (
              <Typography color="text.secondary">No upcoming activities</Typography>
            )}
          </Paper>
        </Grid>

        {/* Upcoming Sales Orders */}
        <Grid item xs={12} lg={6}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon sx={{ mr: 1 }} />
              Upcoming Sales Orders ({salesOrderCount})
            </Typography>
            {salesOrderCount > 0 ? (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {incompleteSalesOrders.map((project) => (
                  <SalesOrderListItem
                    key={project.project_id}
                    project={project}
                    formatDate={formatDate}
                    getStatusColor={getStatusColor}
                    onComplete={handleCompleteSalesOrder}
                    onDirections={openDirections}
                    loadingComplete={loadingComplete}
                  />
                ))}
              </List>
            ) : (
              <Typography color="text.secondary">No upcoming sales orders</Typography>
            )}
          </Paper>
        </Grid>

        {/* Upcoming Purchase Orders */}
        <Grid item xs={12} lg={6}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ShoppingCartIcon sx={{ mr: 1 }} />
              Upcoming Purchase Orders ({purchaseOrderCount})
            </Typography>
            {purchaseOrderCount > 0 ? (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {incompletePurchaseOrders.map((po) => (
                  <PurchaseOrderListItem
                    key={po.purchase_order_id}
                    po={po}
                    formatDate={formatDate}
                    getStatusColor={getStatusColor}
                    onComplete={handleCompletePurchaseOrder}
                    loadingComplete={loadingComplete}
                  />
                ))}
              </List>
            ) : (
              <Typography color="text.secondary">No upcoming purchase orders</Typography>
            )}
          </Paper>
        </Grid>

        {/* Personal To-Do List */}
        <Grid item xs={12} lg={6}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 3, 
              height: '100%',
              borderRadius: 2
            }}
          >
            <PersonalTodoList showIcon={true} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Dashboard;